import { alpha } from '@theme-ui/color'
import ChangelogErrorState from 'components/changelog-page/ChangelogErrorState'
import InternalLink from 'components/UI/InternalLink'
import Svg from 'components/UI/SVG'
import { format, isValid, parseISO } from 'date-fns'
import { graphql, useStaticQuery } from 'gatsby'
import CogIcon from 'images/cog-icon.svg'
import ExclamationIcon from 'images/exclamation-icon.svg'
import GreyIcon from 'images/grey-check.svg'
import Separator from 'images/separator.svg'
import { ReactElement, ReactNode } from 'react'
import { Flex, get, Text, ThemeUIStyleObject } from 'theme-ui'
import useBreakpointIndex from 'theme/useBreakpointIndex'
import getEnglishStrings from 'utils/getEnglishStrings'

function formatDate(date: string | undefined, isMobileOrTablet: boolean): string | undefined {
  const parsedDate = parseISO(date ?? '')
  if (!isValid(parsedDate)) {
    return date
  }

  return date && isMobileOrTablet ? format(parseISO(date), 'dd/MM') : date
}

export function ChangelogWrapper({
  children,
  link,
  linkStyles,
  dataTestId,
}: {
  children: ReactNode
  link: string
  linkStyles?: ThemeUIStyleObject
  dataTestId?: string
}): ReactElement {
  return (
    <InternalLink href={link} sxx={linkStyles} data-testid={dataTestId}>
      <Flex
        sx={{
          flexDirection: 'column',
          border: ({ colors }) => `2px solid ${colors?.white10}`,
          borderRadius: '1.6rem',
          boxSizing: 'border-box',
          overflow: 'hidden',
          minWidth: ['100%', '100%', '46.6rem'],
          transition: theme => `${get(theme, 'transitions.default')}`,
          '& > div': { p: '1.6rem' },
          ':hover': {
            transition: theme => `${get(theme, 'transitions.default')}`,

            '.border-top': {
              borderTop: ({ colors }) => `2px solid ${colors?.white15}`,
              transition: theme => `${get(theme, 'transitions.default')}`,
            },
            border: ({ colors }) => `2px solid ${colors?.white15}`,
            cursor: 'pointer',
            '.header': {
              '&:before': {
                opacity: 1, // Fade in the new gradient
              },
              transition: theme => `${get(theme, 'transitions.default')}`,
            },
            '.arrow': {
              path: {
                transition: theme => `${get(theme, 'transitions.default')}`,
                fill: 'white',
              },
            },
          },
        }}
      >
        {children}
      </Flex>
    </InternalLink>
  )
}

export function ChangelogHeaderWrapper({ children }: { children: ReactNode }): ReactElement {
  return (
    <Flex
      sx={{
        //Using a hack to achieve the gradient effect on a linear gradient
        //https://medium.com/@dave_lunny/animating-css-gradients-using-only-css-d2fd7671e759
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: t =>
            `linear-gradient(0deg, ${alpha('white', 0)(t)}, ${alpha('white', 0.16)(t)})`,
          opacity: 0,
          transition: theme => `${get(theme, 'transitions.default')}`,
        },
        background: t =>
          `linear-gradient(0deg, ${alpha('white', 0)(t)}, ${alpha('white', 0.08)(t)})`,

        transition: theme => `${get(theme, 'transitions.default')}`,
        flexDirection: 'column',
        gap: '1.6rem',
        position: 'relative',
        height: '10rem',
        overflow: 'hidden',
      }}
      className="header"
    >
      {children}
    </Flex>
  )
}

export function ChangelogContentWrapper({ children }: { children: ReactNode }): ReactElement {
  return (
    <Flex
      sx={{
        borderTop: ({ colors }) => `2px solid ${colors?.white10}`,
        backgroundColor: 'transparent',
        minHeight: '6.7rem',
        transition: theme => `${get(theme, 'transitions.default')}`,
      }}
      className="border-top"
    >
      {children}
    </Flex>
  )
}

export function ChangelogHeaderBody({
  version,
  date,
}: {
  version: string | undefined
  date: string | undefined
}): ReactElement {
  return (
    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Flex
        sx={{
          span: {
            variant: 'text.pillsNormal',
          },
        }}
      >
        <Flex
          sx={{
            borderRadius: '1.6rem 0rem 0rem 1.6rem',
            backgroundColor: 'white10',
            p: '0.4rem 0.8rem 0.4rem 1.6rem',
            color: 'white80',
          }}
        >
          <Flex
            sx={{
              gap: '0.8rem',
              alignItems: 'center',
            }}
          >
            <Text>v{version}</Text>
            <Svg svg={Separator} sx={{ maxHeight: '1.6rem' }} />
            <Svg svg={GreyIcon} sx={{ width: '1.6rem', height: '1.6rem' }} />
            <Text>Release</Text>
          </Flex>
        </Flex>
        <Flex
          sx={{
            borderRadius: '0rem 1.6rem 1.6rem 0rem',
            border: ({ colors }) => `1px solid ${colors?.white10}`,
            alignItems: 'center',
            p: '0.4rem 1.6rem 0.4rem 0.8rem',
          }}
        >
          <Text sx={{ color: 'white50' }}>{date}</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}

export function ChangelogContentBody({
  betaVersion,
  guineaVersion,
}: {
  betaVersion: string | undefined
  guineaVersion: string | undefined
}): ReactElement {
  const versionData = useStaticQuery<Queries.ChangelogContentBodyQuery>(graphql`
    query ChangelogContentBody {
      directus {
        general_translations {
          strings {
            languages_code {
              code
            }
            guinea_pig
            beta
          }
        }
      }
    }
  `)

  const versionStrings = getEnglishStrings(versionData.directus?.general_translations?.strings)

  return (
    <Flex
      sx={{
        gap: '0.8rem',
        color: 'white80',
        flexWrap: 'wrap',
        span: {
          variant: 'text.pillsNormal',
        },
        '& > div': {
          borderRadius: '1.6rem',
          border: ({ colors }) => `1px solid ${colors?.white10}`,
          p: '0.4rem 1.6rem',
          gap: '0.8rem',
          alignItems: 'center',
        },
      }}
    >
      <Flex sx={{ alignItems: 'center' }}>
        <Text>v{betaVersion}</Text>
        <Svg svg={Separator} sx={{ maxHeight: '1.6rem' }} />
        <Svg svg={CogIcon} />
        <Text>{versionStrings?.beta}</Text>
      </Flex>
      <Flex>
        <Text>v{guineaVersion}</Text>
        <Svg svg={Separator} sx={{ maxHeight: '1.6rem' }} />
        <Svg svg={ExclamationIcon} />
        <Text>{versionStrings?.guinea_pig}</Text>
      </Flex>
    </Flex>
  )
}

export function ChangelogMobileBody({
  version,
  date,
  isError,
}: {
  version: string | undefined
  date: string | undefined
  isError: boolean
}): ReactElement {
  const breakPoint = useBreakpointIndex()
  const isMobileOrTablet = breakPoint < 2

  if (isError) {
    return <ChangelogErrorState />
  }

  return (
    <Flex sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
      <Flex
        sx={{
          '& > div': { p: '0.4rem 1.6rem 0.4rem 0.8rem' },
          color: 'white50',
          span: {
            variant: 'text.body3',
          },
        }}
      >
        <Flex
          sx={{
            borderRadius: '1.6rem 0rem 0rem 1.6rem',
            backgroundColor: 'white10',
            gap: '0.8rem',
          }}
        >
          <Flex>
            <Text>v{version}</Text>
          </Flex>
        </Flex>
        <Flex
          sx={{
            borderRadius: '0rem 1.6rem 1.6rem 0rem',
            border: ({ colors }) => `1px solid ${colors?.white10}`,
          }}
        >
          <Text>{formatDate(date, isMobileOrTablet)}</Text>
        </Flex>
      </Flex>
    </Flex>
  )
}
